// src/MentorRankingDashboard.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db, auth } from './firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy, limit } from 'firebase/firestore';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, RadarChart, Radar, 
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell 
} from 'recharts';
import { 
   FiUsers, FiAward, FiCalendar, FiTrendingUp, 
  FiDownload, FiUpload, FiFilter, FiSearch, FiRefreshCw 
} from 'react-icons/fi';
import Papa from 'papaparse'; // חבילה קלה לעבודה עם CSV במקום XLSX

// ייבוא CSS
import './MentorRankingDashboard.css';

// סולם צבעים מותאם למדדים השונים
const COLORS = {
  excellent: ['#00C49F', '#4CAF50', '#8BC34A'],
  good: ['#00BCD4', '#03A9F4', '#2196F3'],
  average: ['#FFC107', '#FF9800', '#FF5722'],
  poor: ['#F44336', '#E91E63', '#9C27B0'],
  main: ['#3F51B5', '#673AB7', '#9C27B0', '#E91E63', '#F44336'],
  department: ['#2196F3', '#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFEB3B', '#FFC107', '#FF9800']
};

// מסווג דירוג לפי ציון
const getRankClass = (score) => {
  if (score >= 90) return 'rank-excellent';
  if (score >= 80) return 'rank-good';
  if (score >= 70) return 'rank-average';
  if (score >= 60) return 'rank-below-average';
  return 'rank-poor';
};

// קומפוננטה ראשית של דשבורד דירוג מנטורים
const MentorRankingDashboard = ({ user }) => {
  // === סטייטים ===
  // נתוני מנטורים
  const [mentorData, setMentorData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  
  // פילטרים ומיונים
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [sortConfig, setSortConfig] = useState({ key: 'score', direction: 'desc' });
  const [selectedDepartment, setSelectedDepartment] = useState('כל המחלקות');
  const [searchTerm, setSearchTerm] = useState('');
  
  // לשוניות וכלים
  const [activeTab, setActiveTab] = useState('table');
  const [selectedMetric, setSelectedMetric] = useState('score');
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  
  // מצבי UI
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [highlightedMentor, setHighlightedMentor] = useState(null);
  
  // מצב טעינה תקופתית
  const [historicalData, setHistoricalData] = useState({});
  const [loadingHistorical, setLoadingHistorical] = useState(false);
  const [showHistoricalTrends, setShowHistoricalTrends] = useState(false);
  
  // === קבועים ===
  // חודשים בעברית
  const months = [
    'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
    'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'
  ];

  // שנים להצגה
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
  }, []);

  // מדדי דירוג
  const metrics = [
    { id: 'score', label: 'ציון כולל' },
    { id: 'punctuality', label: 'דייקנות' },
    { id: 'knowledge', label: 'ידע מקצועי' },
    { id: 'feedback', label: 'משוב תלמידים' },
    { id: 'engagement', label: 'מעורבות' },
    { id: 'sessionCount', label: 'מספר שיעורים' }
  ];
  
  // מחלקות
  const departments = useMemo(() => {
    const deptSet = new Set();
    mentorData.forEach(mentor => {
      if (mentor.department) deptSet.add(mentor.department);
    });
    return ['כל המחלקות', ...Array.from(deptSet)];
  }, [mentorData]);

  // === הבאת נתונים ===
  // הבאת נתונים מה-Firestore
  useEffect(() => {
    const fetchMentorData = async () => {
      try {
        setLoading(true);
        setError('');
        
        // פורמט החודש והשנה לפילטור
        const monthStr = (selectedMonth + 1).toString().padStart(2, '0');
        const yearStr = selectedYear.toString();
        const monthYearFilter = `${yearStr}-${monthStr}`;
        
        // בניית השאילתה
        const q = query(
          collection(db, 'mentorRankings'), 
          where('monthYear', '==', monthYearFilter),
          orderBy('score', 'desc')
        );
        
        // ביצוע השאילתה
        const querySnapshot = await getDocs(q);
        const mentors = [];
        
        // איסוף הנתונים
        querySnapshot.forEach((doc) => {
          mentors.push({
            id: doc.id,
            ...doc.data(),
            // הוספת ערכי ברירת מחדל אם חסרים נתונים
            score: doc.data().score || 0,
            punctuality: doc.data().punctuality || 0,
            knowledge: doc.data().knowledge || 0,
            feedback: doc.data().feedback || 0,
            engagement: doc.data().engagement || 0,
            sessionCount: doc.data().sessionCount || 0
          });
        });
        
        // במידה ואין נתונים, ננסה לייצר דוגמה
        if (mentors.length === 0) {
          setSuccess('');
          setError(`לא נמצאו נתונים לחודש ${months[selectedMonth]} ${selectedYear}. אנא העלה קובץ נתונים.`);
        } else {
          setLastUpdate = new Date(Math.max(...mentors.map(m => 
            m.uploadedAt ? new Date(m.uploadedAt).getTime() : 0
          )));
        }
        
        setMentorData(mentors);
        applyFilters(mentors); // הפעלת הפילטרים
        setLoading(false);
      } catch (err) {
        console.error("שגיאה בהבאת נתוני המנטורים:", err);
        setError('אירעה שגיאה בטעינת הנתונים. נסה שוב מאוחר יותר.');
        setLoading(false);
      }
    };

    fetchMentorData();
  }, [selectedMonth, selectedYear]);
  
  // טעינת נתונים היסטוריים למגמות
  const fetchHistoricalData = useCallback(async () => {
    try {
      setLoadingHistorical(true);
      
      // טעינת 6 חודשים אחורה
      const historical = {};
      const currentDate = new Date(selectedYear, selectedMonth);
      
      for (let i = 0; i < 6; i++) {
        const date = new Date(currentDate);
        date.setMonth(date.getMonth() - i);
        
        const monthStr = (date.getMonth() + 1).toString().padStart(2, '0');
        const yearStr = date.getFullYear().toString();
        const monthYearKey = `${yearStr}-${monthStr}`;
        
        const q = query(
          collection(db, 'mentorRankings'), 
          where('monthYear', '==', monthYearKey)
        );
        
        const querySnapshot = await getDocs(q);
        const mentors = [];
        
        querySnapshot.forEach((doc) => {
          mentors.push({ id: doc.id, ...doc.data() });
        });
        
        historical[monthYearKey] = mentors;
      }
      
      setHistoricalData(historical);
      setLoadingHistorical(false);
    } catch (err) {
      console.error("שגיאה בטעינת נתונים היסטוריים:", err);
      setLoadingHistorical(false);
    }
  }, [selectedMonth, selectedYear]);

  // === פילטרים ומיונים ===
  // פונקציה מרכזית להפעלת כל הפילטרים והמיונים
  const applyFilters = useCallback((data = mentorData) => {
    let result = [...data];
    
    // פילטור לפי מחלקה
    if (selectedDepartment !== 'כל המחלקות') {
      result = result.filter(mentor => mentor.department === selectedDepartment);
    }
    
    // פילטור לפי חיפוש טקסט
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(mentor => 
        (mentor.name && mentor.name.toLowerCase().includes(term)) || 
        (mentor.mentorId && mentor.mentorId.toLowerCase().includes(term)) ||
        (mentor.department && mentor.department.toLowerCase().includes(term))
      );
    }
    
    // מיון לפי שדה
    result.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    setFilteredData(result);
  }, [mentorData, selectedDepartment, searchTerm, sortConfig]);

  // עדכון הפילטרים כשהנתונים משתנים
  useEffect(() => {
    applyFilters();
  }, [applyFilters, mentorData, selectedDepartment, searchTerm, sortConfig]);

  // טיפול בשינוי מיון
  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };
  
  // טיפול בחיפוש
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // טיפול בשינוי מחלקה
  const handleDepartmentChange = (e) => {
    setSelectedDepartment(e.target.value);
  };
  
  // === טיפול בקבצים ===
  // טיפול בשינוי קובץ
  const handleFileChange = (e) => {
    setCsvFile(e.target.files[0]);
    setSuccess('');
    setError('');
  };

  // עיבוד והעלאת נתונים מקובץ CSV
  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!csvFile) {
      setError('אנא בחר קובץ להעלאה');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // קריאת קובץ ה-CSV
      const reader = new FileReader();
      
      reader.onload = async (event) => {
        try {
          // פרסור ה-CSV באמצעות PapaParse
          Papa.parse(event.target.result, {
            header: true,
            skipEmptyLines: true,
            complete: async (results) => {
              try {
                if (results.data.length === 0) {
                  throw new Error('הקובץ ריק או בפורמט שגוי');
                }
                
                // הכנת התאריך עבור שמירה
                const monthYearStr = `${selectedYear}-${(selectedMonth + 1).toString().padStart(2, '0')}`;
                
                // הכנת האצווה לכתיבה ל-Firestore
                const batch = [];
                
                // מחיקת הנתונים הקיימים לחודש זה
                const q = query(
                  collection(db, 'mentorRankings'), 
                  where('monthYear', '==', monthYearStr)
                );
                
                const existingDocs = await getDocs(q);
                existingDocs.forEach(doc => {
                  batch.push(deleteDoc(doc.ref));
                });
                
                // הוספת הנתונים החדשים
                for (const row of results.data) {
                  // מיפוי עמודות הקובץ לשדות ב-Firestore
                  const mentorData = {
                    name: row['שם המנטור'] || row['שם'] || '',
                    mentorId: row['מזהה'] || row['ת.ז.'] || '',
                    score: parseFloat(row['ציון כולל'] || 0),
                    punctuality: parseFloat(row['דייקנות'] || 0),
                    knowledge: parseFloat(row['ידע מקצועי'] || 0),
                    feedback: parseFloat(row['משוב תלמידים'] || 0),
                    engagement: parseFloat(row['מעורבות'] || 0),
                    sessionCount: parseInt(row['מספר שיעורים'] || 0),
                    monthYear: monthYearStr,
                    department: row['מחלקה'] || 'כללי',
                    uploadedAt: new Date().toISOString(),
                    uploadedBy: user?.email || 'אנונימי'
                  };
                  
                  // רק אם יש נתונים תקינים נוסיף את המנטור
                  if (mentorData.name) {
                    batch.push(addDoc(collection(db, 'mentorRankings'), mentorData));
                  }
                }
                
                // ביצוע כל הפעולות
                await Promise.all(batch);
                
                setSuccess(`הקובץ הועלה בהצלחה! נוספו ${results.data.length} רשומות.`);
                setShowUploadForm(false);
                setLoading(false);
                
                // טעינה מחדש של הנתונים
                const newQuery = query(
                  collection(db, 'mentorRankings'), 
                  where('monthYear', '==', monthYearStr),
                  orderBy('score', 'desc')
                );
                
                const newSnapshot = await getDocs(newQuery);
                const newMentors = [];
                
                newSnapshot.forEach((doc) => {
                  newMentors.push({
                    id: doc.id,
                    ...doc.data()
                  });
                });
                
                setMentorData(newMentors);
                applyFilters(newMentors);
                
              } catch (err) {
                console.error("שגיאה בעיבוד נתוני CSV:", err);
                setError(`שגיאה בעיבוד נתוני CSV: ${err.message}`);
                setLoading(false);
              }
            },
            error: (err) => {
              console.error("שגיאה בפרסור הקובץ:", err);
              setError(`שגיאה בפרסור הקובץ: ${err}`);
              setLoading(false);
            }
          });
        } catch (err) {
          console.error("שגיאה בקריאת הקובץ:", err);
          setError(`שגיאה בקריאת הקובץ: ${err.message}`);
          setLoading(false);
        }
      };
      
      reader.onerror = (err) => {
        console.error("שגיאה בקריאת הקובץ:", err);
        setError(`שגיאה בקריאת הקובץ`);
        setLoading(false);
      };
      
      reader.readAsText(csvFile);
      
    } catch (err) {
      console.error("שגיאה בהעלאת הקובץ:", err);
      setError(`שגיאה בהעלאת הקובץ: ${err.message}`);
      setLoading(false);
    }
  };
  
  // ייצוא נתונים ל-CSV
  const exportToCSV = () => {
    try {
      // המרת הנתונים לפורמט CSV
      const csv = Papa.unparse(filteredData.map(mentor => ({
        'שם המנטור': mentor.name,
        'מזהה': mentor.mentorId,
        'מחלקה': mentor.department,
        'ציון כולל': mentor.score,
        'דייקנות': mentor.punctuality,
        'ידע מקצועי': mentor.knowledge,
        'משוב תלמידים': mentor.feedback,
        'מעורבות': mentor.engagement,
        'מספר שיעורים': mentor.sessionCount
      })));
      
      // יצירת קובץ להורדה
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `דירוג_מנטורים_${months[selectedMonth]}_${selectedYear}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSuccess('הקובץ יוצא בהצלחה!');
    } catch (err) {
      console.error("שגיאה בייצוא הנתונים:", err);
      setError(`שגיאה בייצוא הנתונים: ${err.message}`);
    }
  };
  
  // === עיבוד נתונים לתרשימים ===
  // נתונים למנטורים מובילים
  const topMentorsData = useMemo(() => {
    return filteredData.slice(0, 5).map(mentor => ({
      name: mentor.name,
      score: mentor.score,
      punctuality: mentor.punctuality,
      knowledge: mentor.knowledge,
      feedback: mentor.feedback,
      engagement: mentor.engagement
    }));
  }, [filteredData]);
  
  // התפלגות ציונים
  const distributionData = useMemo(() => {
    const ranges = [
      { name: '90-100', range: [90, 100], count: 0 },
      { name: '80-89', range: [80, 89], count: 0 },
      { name: '70-79', range: [70, 79], count: 0 },
      { name: '60-69', range: [60, 69], count: 0 },
      { name: '0-59', range: [0, 59], count: 0 },
    ];
    
    filteredData.forEach(mentor => {
      const score = mentor.score;
      for (const rangeItem of ranges) {
        if (score >= rangeItem.range[0] && score <= rangeItem.range[1]) {
          rangeItem.count += 1;
          break;
        }
      }
    });
    
    return ranges;
  }, [filteredData]);
  
  // נתונים לפי מחלקה
  const departmentData = useMemo(() => {
    const departments = {};
    
    filteredData.forEach(mentor => {
      const dept = mentor.department || 'לא ידוע';
      
      if (!departments[dept]) {
        departments[dept] = {
          count: 0,
          scoreSum: 0,
          punctualitySum: 0,
          knowledgeSum: 0,
          feedbackSum: 0,
          engagementSum: 0,
          sessionCountSum: 0
        };
      }
      
      departments[dept].count += 1;
      departments[dept].scoreSum += mentor.score;
      departments[dept].punctualitySum += mentor.punctuality;
      departments[dept].knowledgeSum += mentor.knowledge;
      departments[dept].feedbackSum += mentor.feedback;
      departments[dept].engagementSum += mentor.engagement;
      departments[dept].sessionCountSum += mentor.sessionCount;
    });
    
    return Object.keys(departments).map(dept => ({
      name: dept,
      mentorCount: departments[dept].count,
      score: departments[dept].scoreSum / departments[dept].count,
      punctuality: departments[dept].punctualitySum / departments[dept].count,
      knowledge: departments[dept].knowledgeSum / departments[dept].count,
      feedback: departments[dept].feedbackSum / departments[dept].count,
      engagement: departments[dept].engagementSum / departments[dept].count,
      sessionCount: departments[dept].sessionCountSum / departments[dept].count
    }));
  }, [filteredData]);
  
  // נתונים השוואתיים לפי היסטוריה (6 חודשים אחרונים)
  const historicalTrendData = useMemo(() => {
    if (Object.keys(historicalData).length === 0) return [];
    
    return Object.keys(historicalData).map(monthYear => {
      const [year, month] = monthYear.split('-');
      const displayDate = `${months[parseInt(month) - 1]} ${year}`;
      const mentors = historicalData[monthYear];
      
      if (mentors.length === 0) return { name: displayDate, score: 0 };
      
      // חישוב ממוצעים לחודש
      const avgScore = mentors.reduce((sum, m) => sum + (m.score || 0), 0) / mentors.length;
      const avgPunctuality = mentors.reduce((sum, m) => sum + (m.punctuality || 0), 0) / mentors.length;
      const avgKnowledge = mentors.reduce((sum, m) => sum + (m.knowledge || 0), 0) / mentors.length;
      const avgFeedback = mentors.reduce((sum, m) => sum + (m.feedback || 0), 0) / mentors.length;
      const avgEngagement = mentors.reduce((sum, m) => sum + (m.engagement || 0), 0) / mentors.length;
      
      return {
        name: displayDate,
        score: avgScore,
        punctuality: avgPunctuality,
        knowledge: avgKnowledge,
        feedback: avgFeedback,
        engagement: avgEngagement
      };
    }).sort((a, b) => {
      // מיון כרונולוגי
      const aDate = new Date(a.name.split(' ')[1], months.indexOf(a.name.split(' ')[0]));
      const bDate = new Date(b.name.split(' ')[1], months.indexOf(b.name.split(' ')[0]));
      return aDate - bDate;
    });
  }, [historicalData, months]);
  
  // === רנדור הממשק ===
  if (loading && mentorData.length === 0) {
    return <div className="loading-spinner">טוען נתונים...</div>;
  }

  return (
    <div className="mentor-dashboard-container" dir="rtl">
      {/* כותרת וסרגל כלים */}
      <header className="dashboard-header">
        <div className="header-title-area">
          <h1>
            <FiAward className="header-icon" />
            דירוג צוות מנטורים
          </h1>
          <div className="header-period">
            {months[selectedMonth]} {selectedYear}
          </div>
        </div>
        
        <div className="dashboard-controls">
          <div className="date-selector">
            <label><FiCalendar /> חודש:</label>
            <select 
              value={selectedMonth} 
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            >
              {months.map((month, index) => (
                <option key={index} value={index}>{month}</option>
              ))}
            </select>
            
            <label>שנה:</label>
            <select 
              value={selectedYear} 
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {years.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          
          <div className="search-bar">
            <FiSearch className="search-icon" />
            <input 
              type="text" 
              placeholder="חיפוש מנטור או מחלקה..." 
              value={searchTerm}
              onChange={handleSearch} 
            />
          </div>
          
          <div className="filter-controls">
            <label><FiFilter /> מחלקה:</label>
            <select value={selectedDepartment} onChange={handleDepartmentChange}>
              {departments.map((dept) => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
          
          <div className="action-buttons">
            <button 
              className="action-button upload-button"
              onClick={() => setShowUploadForm(!showUploadForm)}
            >
              <FiUpload />
              {showUploadForm ? 'הסתר טופס' : 'העלאת קובץ'}
            </button>
            
            <button 
              className="action-button export-button"
              onClick={exportToCSV}
              disabled={filteredData.length === 0}
            >
              <FiDownload />
              ייצוא CSV
            </button>
            
            <button 
              className="action-button history-button"
              onClick={() => {
                setShowHistoricalTrends(!showHistoricalTrends);
                if (!showHistoricalTrends && Object.keys(historicalData).length === 0) {
                  fetchHistoricalData();
                }
              }}
            >
              <FiTrendingUp />
              {showHistoricalTrends ? 'הסתר מגמות' : 'הצג מגמות'}
            </button>
          </div>
        </div>
      </header>
      
      {/* הודעות מערכת */}
      {error && (
        <div className="system-message error-message">
          <p>{error}</p>
          {!mentorData.length && (
            <button 
              className="action-button"
              onClick={() => setShowUploadForm(true)}
            >
              <FiUpload /> העלה קובץ עכשיו
            </button>
          )}
        </div>
      )}
      
      {success && (
        <div className="system-message success-message">
          <p>{success}</p>
        </div>
      )}
      
      {/* טופס העלאת קובץ */}
      {showUploadForm && (
        <div className="upload-form">
          <h3><FiUpload /> העלאת קובץ דירוגי מנטורים</h3>
          <div className="upload-instructions">
            <p>יש להעלות קובץ CSV עם הכותרות הבאות:</p>
            <code>שם המנטור, מזהה, מחלקה, ציון כולל, דייקנות, ידע מקצועי, משוב תלמידים, מעורבות, מספר שיעורים</code>
            <p>שימו לב: העלאת קובץ חדש תחליף את כל הנתונים הקיימים לחודש {months[selectedMonth]} {selectedYear}</p>
          </div>
          <form onSubmit={handleUpload}>
            <div className="form-group">
              <label>בחר קובץ CSV:</label>
              <input 
                type="file" 
                accept=".csv" 
                onChange={handleFileChange} 
              />
            </div>
            <button type="submit" className="action-button upload-submit-btn">
              <FiUpload /> העלאה ועדכון נתונים
            </button>
          </form>
        </div>
      )}
      
      {/* לשוניות מידע */}
      <div className="dashboard-tabs">
        <button 
          className={activeTab === 'table' ? 'active' : ''} 
          onClick={() => setActiveTab('table')}
        >
          <FiUsers /> טבלת דירוג
        </button>
        <button 
          className={activeTab === 'charts' ? 'active' : ''} 
          onClick={() => setActiveTab('charts')}
        >
          <FiTrendingUp /> תרשימים ומגמות
        </button>
        <button 
          className={activeTab === 'comparison' ? 'active' : ''} 
          onClick={() => setActiveTab('comparison')}
        >
          <FiFilter /> השוואה לפי מחלקות
        </button>
      </div>
      
      {/* תצוגת הנתונים הנבחרת */}
      <div className="dashboard-content">
        {/* לשונית טבלה */}
        {activeTab === 'table' && (
          <div className="ranking-table-container">
            <div className="metric-selector">
              <span>הצגת דירוג לפי:</span>
              {metrics.map(metric => (
                <button
                  key={metric.id}
                  className={selectedMetric === metric.id ? 'selected' : ''}
                  onClick={() => {
                    setSelectedMetric(metric.id);
                    handleSort(metric.id);
                  }}
                >
                  {metric.label}
                </button>
              ))}
            </div>
            
            {/* תצוגת סיכום */}
            <div className="summary-stats">
              <div className="stat-card">
                <div className="stat-icon">
                  <FiUsers />
                </div>
                <div className="stat-info">
                  <div className="stat-value">{filteredData.length}</div>
                  <div className="stat-label">סה"כ מנטורים</div>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon highlight">
                  <FiAward />
                </div>
                <div className="stat-info">
                  <div className="stat-value">
                    {filteredData[0]?.name || 'אין נתונים'}
                  </div>
                  <div className="stat-label">מנטור מוביל</div>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">
                  <FiTrendingUp />
                </div>
                <div className="stat-info">
                  <div className="stat-value">
                    {filteredData.length ? 
                      (filteredData.reduce((sum, mentor) => sum + mentor.score, 0) / filteredData.length).toFixed(1) : 
                      '0'}
                  </div>
                  <div className="stat-label">ציון ממוצע</div>
                </div>
              </div>
            </div>
            
            {/* טבלת הדירוג */}
            {filteredData.length === 0 ? (
              <div className="no-data">
                <p>לא נמצאו נתונים לחודש זה או לפילטרים שנבחרו</p>
                
                {!mentorData.length && (
                  <button 
                    className="action-button"
                    onClick={() => setShowUploadForm(true)}
                  >
                    <FiUpload /> העלה קובץ עכשיו
                  </button>
                )}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="ranking-table">
                  <thead>
                    <tr>
                      <th className="rank-column">דירוג</th>
                      <th>שם המנטור</th>
                      <th>מחלקה</th>
                      <th 
                        onClick={() => handleSort('score')}
                        className={sortConfig.key === 'score' ? `sorted-${sortConfig.direction}` : ''}
                      >
                        ציון כולל
                      </th>
                      <th 
                        onClick={() => handleSort('punctuality')}
                        className={sortConfig.key === 'punctuality' ? `sorted-${sortConfig.direction}` : ''}
                      >
                        דייקנות
                      </th>
                      <th 
                        onClick={() => handleSort('knowledge')}
                        className={sortConfig.key === 'knowledge' ? `sorted-${sortConfig.direction}` : ''}
                      >
                        ידע מקצועי
                      </th>
                      <th 
                        onClick={() => handleSort('feedback')}
                        className={sortConfig.key === 'feedback' ? `sorted-${sortConfig.direction}` : ''}
                      >
                        משוב תלמידים
                      </th>
                      <th 
                        onClick={() => handleSort('engagement')}
                        className={sortConfig.key === 'engagement' ? `sorted-${sortConfig.direction}` : ''}
                      >
                        מעורבות
                      </th>
                      <th 
                        onClick={() => handleSort('sessionCount')}
                        className={sortConfig.key === 'sessionCount' ? `sorted-${sortConfig.direction}` : ''}
                      >
                        שיעורים
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((mentor, index) => (
                      <tr 
                        key={mentor.id || index}
                        className={highlightedMentor === mentor.id ? 'highlighted-row' : ''}
                        onClick={() => setHighlightedMentor(mentor.id)}
                      >
                        <td className="rank-column">
                          {index < 3 ? (
                            <div className={`rank-medal rank-${index + 1}`}>
                              {index + 1}
                            </div>
                          ) : (
                            index + 1
                          )}
                        </td>
                        <td>{mentor.name}</td>
                        <td>{mentor.department}</td>
                        <td className={getRankClass(mentor.score)}>
                          <div className="score-cell">
                            <div className="score-value">{mentor.score.toFixed(1)}</div>
                            <div className="score-bar" style={{ width: `${mentor.score}%` }}></div>
                          </div>
                        </td>
                        <td className={getRankClass(mentor.punctuality)}>{mentor.punctuality.toFixed(1)}</td>
                        <td className={getRankClass(mentor.knowledge)}>{mentor.knowledge.toFixed(1)}</td>
                        <td className={getRankClass(mentor.feedback)}>{mentor.feedback.toFixed(1)}</td>
                        <td className={getRankClass(mentor.engagement)}>{mentor.engagement.toFixed(1)}</td>
                        <td>{mentor.sessionCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        
        {/* לשונית תרשימים */}
        {activeTab === 'charts' && (
          <div className="charts-container">
            {filteredData.length === 0 ? (
              <div className="no-data">
                <p>לא נמצאו נתונים לחודש זה או לפילטרים שנבחרו</p>
                
                {!mentorData.length && (
                  <button 
                    className="action-button"
                    onClick={() => setShowUploadForm(true)}
                  >
                    <FiUpload /> העלה קובץ עכשיו
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="chart-row">
                  <div className="chart-card">
                    <h3>5 המנטורים המובילים</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={topMentorsData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis domain={[0, 100]} />
                        <Tooltip formatter={(value) => value.toFixed(1)} />
                        <Legend />
                        <Bar dataKey="score" name="ציון כולל" fill="#8884d8">
                          {topMentorsData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS.main[index % COLORS.main.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  
                  <div className="chart-card">
                    <h3>התפלגות ציונים</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={distributionData}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="count"
                          label={({name, count}) => `${name}: ${count}`}
                          animationBegin={0}
                          animationDuration={800}
                        >
                          {distributionData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={index === 0 ? COLORS.excellent[0] : 
                                    index === 1 ? COLORS.good[0] : 
                                    index === 2 ? COLORS.average[0] : 
                                    index === 3 ? COLORS.average[2] : 
                                    COLORS.poor[0]} 
                            />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => value + " מנטורים"} />
                        <Legend verticalAlign="bottom" />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                <div className="chart-row">
                  <div className="chart-card">
                    <h3>השוואת מדדים למנטורים מובילים</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <RadarChart outerRadius={90} data={topMentorsData}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="name" />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} />
                        <Radar name="דייקנות" dataKey="punctuality" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                        <Radar name="ידע מקצועי" dataKey="knowledge" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} />
                        <Radar name="משוב תלמידים" dataKey="feedback" stroke="#ffc658" fill="#ffc658" fillOpacity={0.6} />
                        <Radar name="מעורבות" dataKey="engagement" stroke="#ff8042" fill="#ff8042" fillOpacity={0.6} />
                        <Legend />
                        <Tooltip formatter={(value) => value.toFixed(1)} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                  
                  {showHistoricalTrends ? (
                    <div className="chart-card">
                      <h3>מגמות לאורך זמן</h3>
                      {loadingHistorical ? (
                        <div className="chart-loading">טוען נתונים היסטוריים...</div>
                      ) : historicalTrendData.length > 1 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={historicalTrendData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis domain={[0, 100]} />
                            <Tooltip formatter={(value) => value.toFixed(1)} />
                            <Legend />
                            <Line type="monotone" dataKey="score" name="ציון כולל" stroke="#8884d8" activeDot={{ r: 8 }} />
                            <Line type="monotone" dataKey="punctuality" name="דייקנות" stroke="#82ca9d" />
                            <Line type="monotone" dataKey="knowledge" name="ידע מקצועי" stroke="#ffc658" />
                            <Line type="monotone" dataKey="feedback" name="משוב תלמידים" stroke="#ff8042" />
                            <Line type="monotone" dataKey="engagement" name="מעורבות" stroke="#0088FE" />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="no-historical-data">
                          <p>אין מספיק נתונים היסטוריים להצגת מגמות</p>
                          <p>העלה נתונים לחודשים קודמים כדי לראות את המגמות</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="chart-card">
                      <h3>ניתוח מדדים מצטיינים ולקויים</h3>
                      <div className="score-analysis">
                        <div className="score-section">
                          <h4>מדדים מצטיינים</h4>
                          <div className="score-bars">
                            {metrics.slice(1, 5)
                              .map(metric => ({
                                id: metric.id,
                                label: metric.label,
                                avg: filteredData.reduce((sum, mentor) => sum + mentor[metric.id], 0) / filteredData.length
                              }))
                              .sort((a, b) => b.avg - a.avg)
                              .slice(0, 3)
                              .map((metric, index) => (
                                <div key={metric.id} className="metric-bar-container">
                                  <div className="metric-label">{metric.label}</div>
                                  <div className="metric-bar-wrapper">
                                    <div 
                                      className="metric-bar excellent-bar"
                                      style={{ width: `${metric.avg}%` }}
                                    ></div>
                                    <div className="metric-value">{metric.avg.toFixed(1)}</div>
                                  </div>
                                </div>
                              ))
                            }
                          </div>
                        </div>
                        
                        <div className="score-section">
                          <h4>מדדים לשיפור</h4>
                          <div className="score-bars">
                            {metrics.slice(1, 5)
                              .map(metric => ({
                                id: metric.id,
                                label: metric.label,
                                avg: filteredData.reduce((sum, mentor) => sum + mentor[metric.id], 0) / filteredData.length
                              }))
                              .sort((a, b) => a.avg - b.avg)
                              .slice(0, 3)
                              .map((metric, index) => (
                                <div key={metric.id} className="metric-bar-container">
                                  <div className="metric-label">{metric.label}</div>
                                  <div className="metric-bar-wrapper">
                                    <div 
                                      className="metric-bar needs-improvement-bar"
                                      style={{ width: `${metric.avg}%` }}
                                    ></div>
                                    <div className="metric-value">{metric.avg.toFixed(1)}</div>
                                  </div>
                                </div>
                              ))
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="chart-row">
                  <div className="chart-card full-width">
                    <h3>ניתוח מעמיק לפי מדדים</h3>
                    <div className="metric-scatter-container">
                      <div className="metric-selectors">
                        <p>הציון הכולל של המנטורים מחושב משקלול של כלל המדדים</p>
                        <p>בתרשים ניתן לראות את הקשר בין הציון הכולל והמדדים השונים</p>
                      </div>
                      
                      <ResponsiveContainer width="100%" height={350}>
                        <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                          <CartesianGrid />
                          <XAxis 
                            type="number" 
                            dataKey={selectedMetric === 'score' ? 'punctuality' : selectedMetric} 
                            name={selectedMetric === 'score' ? 'דייקנות' : metrics.find(m => m.id === selectedMetric)?.label} 
                            domain={[0, 100]} 
                          />
                          <YAxis 
                            type="number" 
                            dataKey="score" 
                            name="ציון כולל" 
                            domain={[0, 100]} 
                          />
                          <Tooltip 
                            cursor={{ strokeDasharray: '3 3' }} 
                            formatter={(value) => value.toFixed(1)}
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <div className="custom-tooltip">
                                    <p className="tooltip-name">{data.name}</p>
                                    <p className="tooltip-score">ציון כולל: {data.score.toFixed(1)}</p>
                                    <p className="tooltip-metric">
                                      {selectedMetric === 'score' ? 'דייקנות' : metrics.find(m => m.id === selectedMetric)?.label}: 
                                      {' '}
                                      {data[selectedMetric === 'score' ? 'punctuality' : selectedMetric].toFixed(1)}
                                    </p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Scatter 
                            name="מנטורים" 
                            data={filteredData.map(mentor => ({
                              name: mentor.name,
                              score: mentor.score,
                              punctuality: mentor.punctuality,
                              knowledge: mentor.knowledge,
                              feedback: mentor.feedback,
                              engagement: mentor.engagement
                            }))} 
                            fill="#8884d8"
                          >
                            {filteredData.map((item, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={getRankClass(item.score) === 'rank-excellent' ? COLORS.excellent[0] :
                                      getRankClass(item.score) === 'rank-good' ? COLORS.good[0] :
                                      getRankClass(item.score) === 'rank-average' ? COLORS.average[0] :
                                      getRankClass(item.score) === 'rank-below-average' ? COLORS.average[2] :
                                      COLORS.poor[0]}
                              />
                            ))}
                          </Scatter>
                        </ScatterChart>
                      </ResponsiveContainer>
                      
                      <div className="metric-buttons">
                        {metrics.slice(1, 5).map(metric => (
                          <button
                            key={metric.id}
                            className={selectedMetric === metric.id ? 'selected' : ''}
                            onClick={() => setSelectedMetric(metric.id)}
                          >
                            {metric.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        
        {/* לשונית השוואת מחלקות */}
        {activeTab === 'comparison' && (
          <div className="comparison-container">
            {filteredData.length === 0 ? (
              <div className="no-data">
                <p>לא נמצאו נתונים לחודש זה או לפילטרים שנבחרו</p>
                
                {!mentorData.length && (
                  <button 
                    className="action-button"
                    onClick={() => setShowUploadForm(true)}
                  >
                    <FiUpload /> העלה קובץ עכשיו
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="chart-row">
                  <div className="chart-card full-width">
                    <h3>השוואת ציונים ממוצעים לפי מחלקות</h3>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={departmentData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis yAxisId="left" orientation="left" domain={[0, 100]} />
                        <YAxis yAxisId="right" orientation="right" domain={[0, Math.max(...departmentData.map(d => d.mentorCount)) * 1.2]} />
                        <Tooltip formatter={(value, name) => {
                          if (name === 'mentorCount') return value + ' מנטורים';
                          return value.toFixed(1);
                        }} />
                        <Legend />
                        <Bar yAxisId="left" dataKey="score" name="ציון כולל" fill="#8884d8" />
                        <Bar yAxisId="left" dataKey="punctuality" name="דייקנות" fill="#82ca9d" />
                        <Bar yAxisId="left" dataKey="knowledge" name="ידע מקצועי" fill="#ffc658" />
                        <Bar yAxisId="left" dataKey="feedback" name="משוב תלמידים" fill="#ff8042" />
                        <Bar yAxisId="left" dataKey="engagement" name="מעורבות" fill="#0088FE" />
                        <Bar yAxisId="right" dataKey="mentorCount" name="מספר מנטורים" fill="#FF6384" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                <div className="chart-row">
                  <div className="chart-card">
                    <h3>כמות מנטורים לפי מחלקה</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={departmentData}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="mentorCount"
                          nameKey="name"
                          label={({name, mentorCount}) => `${name}: ${mentorCount}`}
                          animationBegin={0}
                          animationDuration={800}
                        >
                          {departmentData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={COLORS.department[index % COLORS.department.length]} 
                            />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => value + " מנטורים"} />
                        <Legend layout="vertical" verticalAlign="middle" align="right" />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  
                  <div className="chart-card">
                    <h3>דירוג המחלקות</h3>
                    <div className="department-ranking">
                      {departmentData
                        .filter(dept => dept.mentorCount >= 2) // מסנן מחלקות עם לפחות 2 מנטורים
                        .sort((a, b) => b.score - a.score)
                        .map((dept, index) => (
                          <div key={dept.name} className="department-rank-item">
                            <div className="department-rank-header">
                              <div className="department-rank-position">{index + 1}</div>
                              <div className="department-rank-name">{dept.name}</div>
                              <div className={`department-rank-score ${getRankClass(dept.score)}`}>{dept.score.toFixed(1)}</div>
                            </div>
                            <div className="department-rank-metrics">
                              <div className="department-metric">
                                <div className="metric-name">דייקנות:</div>
                                <div className="metric-value">{dept.punctuality.toFixed(1)}</div>
                              </div>
                              <div className="department-metric">
                                <div className="metric-name">ידע מקצועי:</div>
                                <div className="metric-value">{dept.knowledge.toFixed(1)}</div>
                              </div>
                              <div className="department-metric">
                                <div className="metric-name">משוב תלמידים:</div>
                                <div className="metric-value">{dept.feedback.toFixed(1)}</div>
                              </div>
                              <div className="department-metric">
                                <div className="metric-name">מעורבות:</div>
                                <div className="metric-value">{dept.engagement.toFixed(1)}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
                
                <div className="chart-row">
                  <div className="chart-card full-width">
                    <h3>טבלת השוואת מחלקות</h3>
                    <div className="table-responsive">
                      <table className="department-table">
                        <thead>
                          <tr>
                            <th>מחלקה</th>
                            <th>מספר מנטורים</th>
                            <th>ממוצע ציון כולל</th>
                            <th>דייקנות</th>
                            <th>ידע מקצועי</th>
                            <th>משוב תלמידים</th>
                            <th>מעורבות</th>
                            <th>מספר שיעורים ממוצע</th>
                          </tr>
                        </thead>
                        <tbody>
                          {departmentData
                            .sort((a, b) => b.score - a.score)
                            .map(dept => (
                              <tr key={dept.name}>
                                <td>{dept.name}</td>
                                <td>{dept.mentorCount}</td>
                                <td className={getRankClass(dept.score)}>{dept.score.toFixed(1)}</td>
                                <td className={getRankClass(dept.punctuality)}>{dept.punctuality.toFixed(1)}</td>
                                <td className={getRankClass(dept.knowledge)}>{dept.knowledge.toFixed(1)}</td>
                                <td className={getRankClass(dept.feedback)}>{dept.feedback.toFixed(1)}</td>
                                <td className={getRankClass(dept.engagement)}>{dept.engagement.toFixed(1)}</td>
                                <td>{Math.round(dept.sessionCount)}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      
      {/* פוטר */}
      <footer className="dashboard-footer">
        <div className="footer-info">
          <div>מערכת דירוג צוות מנטורים</div>
          <div>עודכן לאחרונה: {lastUpdate ? new Date(lastUpdate).toLocaleDateString('he-IL') : 'לא ידוע'}</div>
        </div>
        
        <div className="footer-actions">
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            חזרה למעלה
          </button>
          <button onClick={() => setShowUploadForm(true)}>
            <FiUpload /> העלאת נתונים
          </button>
        </div>
      </footer>
    </div>
  );
};

export default MentorRankingDashboard;